import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NavigationStart, Router } from '@angular/router';
import { fromEvent, Subscription } from 'rxjs';
import { map, startWith, pairwise, filter } from 'rxjs/operators';
import { takeUntilDestroy, TakeUntilDestroy } from '@mona/shared/utils';
import { Animations } from '@mona/ui/animations';
import { requireInput } from '../../utils/utils';

/**
 * [UserMenu Component](https://brightlayer-ui-components.github.io/angular/?path=/info/components-user-menu--readme)
 *
 * The `<ui-user-menu>` is an Avatar that opens a Menu when clicked.
 * It is typically used in the top-right corner of an application and indicates who is logged in.
 */
@TakeUntilDestroy
@Component({
    selector: 'ui-user-menu',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./user-menu.component.scss'],
    template: `
        <!-- This button triggers the overlay and is it's origin -->
        <ui-user-menu-avatar
            cdkOverlayOrigin
            [avatarValue]="avatarValue"
            [avatarImage]="avatarImage"
            [ngClass]="{ 'selected-avatar': isOnUserSettingsPage$ | async }"
            (click)="openOverlay()"
            #trigger="cdkOverlayOrigin"
        >
            <ng-content select="[ui-avatar]"></ng-content>
        </ui-user-menu-avatar>

        <ng-template #menu>
            <ui-user-menu-header
                *ngIf="menuTitle"
                class="ui-user-menu-header"
                [title]="menuTitle"
                [subtitle]="menuSubtitle"
                [divider]="true"
            >
                <ui-user-menu-avatar
                    ui-icon
                    [avatarValue]="avatarValue"
                    [avatarImage]="avatarImage"
                    class="ui-user-menu-header-avatar"
                >
                    <ng-content select="[ui-menu-avatar]"></ng-content>
                </ui-user-menu-avatar>
            </ui-user-menu-header>
            <ng-content select="[ui-menu-header]"></ng-content>
            <ng-content select="[ui-menu-body]"></ng-content>
        </ng-template>

        <ng-template
            cdkConnectedOverlay
            (overlayKeydown)="dismissViaEscape($event)"
            (backdropClick)="onClickMenuBackdrop()"
            [cdkConnectedOverlayPush]="true"
            [cdkConnectedOverlayHasBackdrop]="true"
            [cdkConnectedOverlayOrigin]="trigger"
            [cdkConnectedOverlayOpen]="isMenuOpen"
            [cdkConnectedOverlayPositions]="positions"
            [cdkConnectedOverlayViewportMargin]="16"
            [cdkConnectedOverlayBackdropClass]="'ui-user-menu-overlay-backdrop'"
        >
            <mat-card class="ui-user-menu-overlay mat-elevation-z8" [@fadeInOut]>
                <ng-template [ngTemplateOutlet]="menu"></ng-template>
            </mat-card>
        </ng-template>
    `,
    host: {
        class: 'ui-user-menu',
    },
    animations: [Animations.fadeInOut],
})
export class UserMenuComponent implements OnInit, OnChanges, OnDestroy {
    /** Image source for avatar */
    @Input() avatarImage: string;

    /** Text value for avatar */
    @Input() avatarValue: string;

    /** Subtitle shown when menu is open */
    @Input() menuSubtitle: string;

    /** Title shown when menu is open */
    @Input() menuTitle: string;

    /** Where to render the menu relative to the avatar */
    @Input() positions = [
        new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'top' }),
    ];

    /** Whether the menu overlay appears on screen. */
    @Input() open;

    /**
     * Window pixel width at which the responsive bottom sheet menu is triggered (set to 0 to disable responsive behavior)
     *
     * @default 600
     */
    @Input() useBottomSheetAt = 600;

    /** Emits event when backdrop is clicked */
    @Output() backdropClick: EventEmitter<void> = new EventEmitter<void>();

    /** Emits an event when the open prop changes */
    @Output() openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild(TemplateRef) menu: TemplateRef<any>;

    /**
     * INFO: add comment
     *
     * @param _bottomSheet
     * @param cdRef
     * @param router
     */
    constructor(
        private readonly _bottomSheet: MatBottomSheet,
        private readonly cdRef: ChangeDetectorRef,
        private router: Router,
    ) {}

    screenSizeChangeListener: Subscription;
    useBottomSheet: boolean;
    isMenuOpen: boolean;
    isBottomSheetDismissing: boolean;
    isOnUserSettingsPage$ = this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
        map(event => {
            if (event instanceof NavigationStart) {
                return event?.url.includes('/user-settings');
            }
        }),
        startWith(false),
        pairwise(),
        filter(([prev, curr]) => prev !== curr),
        map(([, curr]) => curr),
    );

    checkScreenSize = (): boolean => document.body.offsetWidth < this.useBottomSheetAt;

    /**
     * INFO: add comment
     */
    ngOnInit(): void {
        requireInput<UserMenuComponent>(['open'], this);

        // Subscribe to resize events and transition from menu to bottomsheet (or vise versa) when open & resizing
        this.screenSizeChangeListener = fromEvent(window, 'resize')
            .pipe(map(this.checkScreenSize))
            .pipe(startWith(this.checkScreenSize()))
            .subscribe((isMobile: boolean) => {
                // Transition from Desktop to Mobile
                if (this.open && isMobile && !this.useBottomSheet) {
                    this.isMenuOpen = false;
                    this.openBottomSheet();
                    this.cdRef.detectChanges();
                }
                // Transition from Mobile to Desktop
                else if (this.open && !isMobile && this.useBottomSheet) {
                    this._bottomSheet.dismiss(true);
                }
                this.useBottomSheet = isMobile;
            });
    }

    /**
     * INFO: add comment
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Set state and dismiss bottom sheet when open() changes.
        if (changes.open) {
            // State changes from closed to open.
            const openState = changes.open;
            if (openState.currentValue === true && (openState.previousValue === false || openState.isFirstChange())) {
                if (this.useBottomSheet) {
                    this.openBottomSheet();
                } else {
                    this.isMenuOpen = true;
                }
            }
            // State changes from open to closed.
            if (openState.currentValue === false && (openState.previousValue === true || openState.isFirstChange())) {
                this.isMenuOpen = false;
                this._bottomSheet.dismiss(false);
            }
        }

        if (changes.useBottomSheetAt) {
            this.useBottomSheet = this.checkScreenSize();
        }
    }

    /**
     * INFO: add comment
     */
    ngOnDestroy(): void {
        if (this.screenSizeChangeListener) {
            this.screenSizeChangeListener.unsubscribe();
        }
    }

    /**
     * INFO: add comment
     */
    onClickMenuBackdrop(): void {
        this.openChange.emit(false);
        this.backdropClick.emit();
    }

    /**
     * INFO: add comment
     */
    openOverlay(): void {
        this.router.navigate(['user-settings']);
        // If the bottom sheet is being dismissed, ignore subsequent open calls.
        if (this.isBottomSheetDismissing) {
            return;
        }
        this.openChange.emit(true);
    }

    /**
     * INFO: add comment
     */
    openBottomSheet(): void {
        // Do not invoke the bottom sheet until `menu` has been rendered.
        this.cdRef.detectChanges();

        const bottomSheetRef = this._bottomSheet.open(this.menu, {
            backdropClass: 'ui-user-menu-bottomsheet-backdrop',
            panelClass: 'ui-user-menu-bottomsheet',
            hasBackdrop: true,
        });

        bottomSheetRef.afterDismissed().subscribe((openMenu: boolean | undefined) => {
            this.isBottomSheetDismissing = false;
            this.isMenuOpen = Boolean(openMenu);
            this.cdRef.detectChanges();
        });

        bottomSheetRef.keydownEvents().subscribe((key: KeyboardEvent) => {
            this.dismissViaEscape(key);
        });

        bottomSheetRef.backdropClick().subscribe(() => {
            this.isBottomSheetDismissing = true;
            this.openChange.emit(false);
            this.backdropClick.emit();
        });
    }

    /**
     * INFO: add comment
     *
     * @param e
     */
    dismissViaEscape(e: KeyboardEvent): void {
        if (e && e.key && e.key.toLowerCase() === 'escape') {
            this.openChange.emit(false);
        }
    }
}
